import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  ReactNode,
} from 'react';
import notificationService, {
  NotificationRecord,
  NotificationType,
} from '@services/notificationService';
import { useAuth } from './AuthContext';

interface NotificationContextType {
  // State
  isSupported: boolean;
  isInitialized: boolean;
  permission: NotificationPermission;
  isSubscribed: boolean;
  notifications: NotificationRecord[];
  unreadCount: number;
  isLoading: boolean;

  // Actions
  initialize: () => Promise<void>;
  requestPermission: () => Promise<NotificationPermission>;
  subscribe: (deviceName?: string) => Promise<boolean>;
  unsubscribe: () => Promise<boolean>;
  refreshNotifications: () => Promise<void>;
  markAsRead: (notificationId: string) => Promise<void>;
  markAllAsRead: () => Promise<void>;
  sendTestNotification: () => Promise<boolean>;
  getNotificationIcon: (type: NotificationType) => string;
  getNotificationColor: (type: NotificationType) => string;
}

const NotificationContext = createContext<NotificationContextType | undefined>(
  undefined
);

export const useNotifications = (): NotificationContextType => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error(
      'useNotifications must be used within a NotificationProvider'
    );
  }
  return context;
};

interface NotificationProviderProps {
  children: ReactNode;
}

export const NotificationProvider: React.FC<NotificationProviderProps> = ({
  children,
}) => {
  const { user } = useAuth();
  
  const [isSupported, setIsSupported] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  const [permission, setPermission] = useState<NotificationPermission>('default');
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [notifications, setNotifications] = useState<NotificationRecord[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);

  // Initialize notification service
  const initialize = useCallback(async () => {
    try {
      const supported = notificationService.isSupported();
      setIsSupported(supported);

      if (!supported) {
        console.warn('Push notifications not supported');
        return;
      }

      const initialized = await notificationService.initialize();
      setIsInitialized(initialized);

      if (initialized) {
        setPermission(notificationService.getPermissionStatus());
        const subscribed = await notificationService.isSubscribed();
        setIsSubscribed(subscribed);
      }
    } catch (error) {
      console.error('Failed to initialize notifications:', error);
    }
  }, []);

  // Request permission
  const requestPermission = useCallback(async () => {
    const newPermission = await notificationService.requestPermission();
    setPermission(newPermission);
    return newPermission;
  }, []);

  // Subscribe to notifications
  const subscribe = useCallback(async (deviceName?: string) => {
    setIsLoading(true);
    try {
      const subscription = await notificationService.subscribe(deviceName);
      const success = subscription !== null;
      setIsSubscribed(success);
      return success;
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Unsubscribe from notifications
  const unsubscribe = useCallback(async () => {
    setIsLoading(true);
    try {
      const success = await notificationService.unsubscribe();
      if (success) {
        setIsSubscribed(false);
      }
      return success;
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Refresh notifications list
  const refreshNotifications = useCallback(async () => {
    if (!user) return;
    
    setIsLoading(true);
    try {
      const response = await notificationService.getNotifications(50);
      setNotifications(response.notifications);
      setUnreadCount(response.unreadCount);
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
    } finally {
      setIsLoading(false);
    }
  }, [user]);

  // Mark notification as read
  const markAsRead = useCallback(async (notificationId: string) => {
    const success = await notificationService.markAsRead(notificationId);
    if (success) {
      setNotifications((prev) =>
        prev.map((n) =>
          n.id === notificationId ? { ...n, isRead: true } : n
        )
      );
      setUnreadCount((prev) => Math.max(0, prev - 1));
    }
  }, []);

  // Mark all as read
  const markAllAsRead = useCallback(async () => {
    await notificationService.markAllAsRead();
    setNotifications((prev) => prev.map((n) => ({ ...n, isRead: true })));
    setUnreadCount(0);
  }, []);

  // Send test notification
  const sendTestNotification = useCallback(async () => {
    return await notificationService.sendTestNotification();
  }, []);

  // Get notification icon based on type
  const getNotificationIcon = useCallback((type: NotificationType): string => {
    const icons: Record<NotificationType, string> = {
      [NotificationType.AppointmentScheduled]: '📅',
      [NotificationType.AppointmentReminder]: '⏰',
      [NotificationType.AppointmentCancelled]: '❌',
      [NotificationType.AppointmentConfirmed]: '✅',
      [NotificationType.PrescriptionCreated]: '💊',
      [NotificationType.LabReportReady]: '🧪',
      [NotificationType.RadiologyReportReady]: '🔬',
      [NotificationType.VaccinationReminder]: '💉',
      [NotificationType.DataSharingRequest]: '🔗',
      [NotificationType.System]: '🔔',
      [NotificationType.Custom]: '📢',
    };
    return icons[type] || '🔔';
  }, []);

  // Get notification color based on type
  const getNotificationColor = useCallback((type: NotificationType): string => {
    const colors: Record<NotificationType, string> = {
      [NotificationType.AppointmentScheduled]: '#2196f3',
      [NotificationType.AppointmentReminder]: '#ff9800',
      [NotificationType.AppointmentCancelled]: '#f44336',
      [NotificationType.AppointmentConfirmed]: '#4caf50',
      [NotificationType.PrescriptionCreated]: '#9c27b0',
      [NotificationType.LabReportReady]: '#00bcd4',
      [NotificationType.RadiologyReportReady]: '#3f51b5',
      [NotificationType.VaccinationReminder]: '#e91e63',
      [NotificationType.DataSharingRequest]: '#607d8b',
      [NotificationType.System]: '#795548',
      [NotificationType.Custom]: '#673ab7',
    };
    return colors[type] || '#9e9e9e';
  }, []);

  // Initialize on mount
  useEffect(() => {
    initialize();
  }, [initialize]);

  // Fetch notifications once when user is authenticated (initial load only)
  useEffect(() => {
    if (user) {
      refreshNotifications();
    }
  }, [user, refreshNotifications]);

  // Listen for service worker messages
  useEffect(() => {
    if (!isSupported) return;

    const handleMessage = (event: MessageEvent) => {
      if (event.data?.type === 'NOTIFICATION_RECEIVED') {
        refreshNotifications();
      }
    };

    navigator.serviceWorker?.addEventListener('message', handleMessage);
    return () => {
      navigator.serviceWorker?.removeEventListener('message', handleMessage);
    };
  }, [isSupported, refreshNotifications]);

  const value: NotificationContextType = {
    isSupported,
    isInitialized,
    permission,
    isSubscribed,
    notifications,
    unreadCount,
    isLoading,
    initialize,
    requestPermission,
    subscribe,
    unsubscribe,
    refreshNotifications,
    markAsRead,
    markAllAsRead,
    sendTestNotification,
    getNotificationIcon,
    getNotificationColor,
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};

export default NotificationContext;
